import type {
  ConditionalFormatRule,
  ConditionalOperator,
  ScalarValue,
  VisualizationSpec,
} from '@aibi/contracts';
import type { CSSProperties } from 'react';

const SEMAPHORE = {
  red: '#DC2626',
  amber: '#CA8A04',
  green: '#16A34A',
  redBg: '#FEE2E2',
  amberBg: '#FEF3C7',
  greenBg: '#DCFCE7',
} as const;

const POS_NEG = {
  positive: '#16A34A',
  negative: '#DC2626',
  zero: '#64748B',
} as const;

const BOOL = {
  yes: '#16A34A',
  no: '#DC2626',
  yesBg: '#DCFCE7',
  noBg: '#FEE2E2',
} as const;

export type ConditionalPresetId = 'semaforo' | 'positivo-negativo' | 'si-no';

export const CONDITIONAL_PRESET_LABELS: Readonly<Record<ConditionalPresetId, string>> = {
  semaforo: 'Semaforo (rojo / amarillo / verde)',
  'positivo-negativo': 'Positivo / negativo',
  'si-no': 'Si / No (booleano)',
};

export const OPERATOR_LABELS: Readonly<Record<ConditionalOperator, string>> = {
  eq: 'Igual a',
  neq: 'Distinto de',
  gt: 'Mayor que',
  gte: 'Mayor o igual',
  lt: 'Menor que',
  lte: 'Menor o igual',
  between: 'Entre',
  in: 'En lista',
  contains: 'Contiene',
  isEmpty: 'Vacio',
};

const newRuleId = (): string => `cf.${crypto.randomUUID()}`;

const isEmptyValue = (value: ScalarValue): boolean => value === null || value === '';

const asNumber = (value: ScalarValue): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (typeof value === 'boolean') return value ? 1 : 0;
  return null;
};

const asText = (value: ScalarValue): string => {
  if (value === null) return '';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return String(value);
};

const operandsEqual = (left: ScalarValue, right: string | number | boolean): boolean => {
  if (typeof right === 'boolean') {
    if (typeof left === 'boolean') return left === right;
    if (typeof left === 'number') return (left !== 0) === right;
    if (typeof left === 'string') {
      const normalized = left.trim().toLowerCase();
      return right
        ? ['true', '1', 'si', 'sí', 'yes'].includes(normalized)
        : ['false', '0', 'no'].includes(normalized);
    }
    return false;
  }
  if (typeof right === 'number') {
    const leftNumber = asNumber(left);
    return leftNumber !== null && leftNumber === right;
  }
  return asText(left).toLowerCase() === right.toLowerCase();
};

/** Primera regla que coincide gana (orden de la lista). */
export function matchConditionalRule(
  rules: readonly ConditionalFormatRule[],
  key: string,
  value: ScalarValue,
): ConditionalFormatRule | null {
  for (const rule of rules) {
    if (rule.key !== key) continue;
    if (matchesOperator(rule.operator, value, rule.values)) return rule;
  }
  return null;
}

export function matchesOperator(
  operator: ConditionalOperator,
  value: ScalarValue,
  operands: readonly (string | number | boolean)[],
): boolean {
  switch (operator) {
    case 'isEmpty':
      return isEmptyValue(value);
    case 'eq':
      return operands[0] !== undefined && operandsEqual(value, operands[0]);
    case 'neq':
      return operands[0] !== undefined && !operandsEqual(value, operands[0]);
    case 'contains': {
      const needle = operands[0];
      if (typeof needle !== 'string') return false;
      return asText(value).toLowerCase().includes(needle.toLowerCase());
    }
    case 'in':
      return operands.some((operand) => operandsEqual(value, operand));
    case 'gt':
    case 'gte':
    case 'lt':
    case 'lte':
    case 'between': {
      const left = asNumber(value);
      if (left === null) return false;
      if (operator === 'between') {
        const low = typeof operands[0] === 'number' ? operands[0] : null;
        const high = typeof operands[1] === 'number' ? operands[1] : null;
        return low !== null && high !== null && left >= Math.min(low, high) && left <= Math.max(low, high);
      }
      const right = typeof operands[0] === 'number' ? operands[0] : null;
      if (right === null) return false;
      if (operator === 'gt') return left > right;
      if (operator === 'gte') return left >= right;
      if (operator === 'lt') return left < right;
      return left <= right;
    }
  }
}

/** Estilo CSS de celda/KPI derivado de la primera regla aplicable. */
export function conditionalStyleOf(
  spec: VisualizationSpec,
  key: string,
  value: ScalarValue,
): CSSProperties | undefined {
  const rule = matchConditionalRule(spec.formatting.conditional, key, value);
  if (!rule) return undefined;
  const style: CSSProperties = {};
  if (rule.color !== undefined) style.color = rule.color;
  if (rule.background !== undefined) style.backgroundColor = rule.background;
  return rule.color === undefined && rule.background === undefined ? undefined : style;
}

/** Color de relleno para un punto/barra (prioriza fondo, luego texto). */
export function conditionalFillOf(
  spec: VisualizationSpec,
  key: string,
  value: ScalarValue,
): string | undefined {
  const rule = matchConditionalRule(spec.formatting.conditional, key, value);
  return rule?.background ?? rule?.color;
}

export function buildSemaphoreRules(
  key: string,
  low: number,
  high: number,
): ConditionalFormatRule[] {
  const floor = Math.min(low, high);
  const ceiling = Math.max(low, high);
  return [
    {
      id: newRuleId(),
      key,
      operator: 'lt',
      values: [floor],
      color: SEMAPHORE.red,
      background: SEMAPHORE.redBg,
    },
    {
      id: newRuleId(),
      key,
      operator: 'gte',
      values: [ceiling],
      color: SEMAPHORE.green,
      background: SEMAPHORE.greenBg,
    },
    {
      id: newRuleId(),
      key,
      operator: 'between',
      values: [floor, ceiling],
      color: SEMAPHORE.amber,
      background: SEMAPHORE.amberBg,
    },
  ];
}

export function buildPositiveNegativeRules(key: string): ConditionalFormatRule[] {
  return [
    {
      id: newRuleId(),
      key,
      operator: 'gt',
      values: [0],
      color: POS_NEG.positive,
    },
    {
      id: newRuleId(),
      key,
      operator: 'lt',
      values: [0],
      color: POS_NEG.negative,
    },
    {
      id: newRuleId(),
      key,
      operator: 'eq',
      values: [0],
      color: POS_NEG.zero,
    },
  ];
}

export function buildBooleanRules(key: string): ConditionalFormatRule[] {
  return [
    {
      id: newRuleId(),
      key,
      operator: 'eq',
      values: [true],
      color: BOOL.yes,
      background: BOOL.yesBg,
    },
    {
      id: newRuleId(),
      key,
      operator: 'eq',
      values: [false],
      color: BOOL.no,
      background: BOOL.noBg,
    },
  ];
}

export function createBlankRule(key: string): ConditionalFormatRule {
  return {
    id: newRuleId(),
    key,
    operator: 'gte',
    values: [0],
    color: SEMAPHORE.green,
  };
}
